import { AbstractControl, ValidationErrors } from '@angular/forms';
import { isNotNullOrUndefined, isNullOrUndefined } from './typeUtils';
import { DateService } from '../services/date.service';
import { EmailValidator } from 'commons-validator-js';
import { SubmissionDataFormGroup } from '../../pages/submission/submission-page/submission-page.forms';

export const STREET_MAX_LENGTH = 40;
export const HOUSE_NUMBER_MAX_LENGTH = 10;

const NAME_MAX_LENGTH = 60;

const BLOCKED_DOMAINS = ['@visana.ch', '@ssmpartner.ch', '@maklerzentrum.ch', '@miberatung.ch'];

export const dateOfBirthPatternValidator = (dateOfBirth: AbstractControl): ValidationErrors | null => {
  if (!DateService.isValidViewDate(dateOfBirth.value)) {
    return { dateOfBirth: 'INVALID_DATE' };
  }
  return null;
};

export const adultValidator = (dateOfBirth: AbstractControl): ValidationErrors | null => {
  if (!DateService.isAdult(DateService.parseDateFromView(dateOfBirth.value))) {
    return { dateOfBirth: 'IS_NOT_ADULT' };
  }
  return null;
};

export const firstNamePatternValidator = (control: AbstractControl): ValidationErrors | null => {
  return internationalNamePatternValidator(control, NAME_MAX_LENGTH);
};

export const lastNamePatternValidator = (control: AbstractControl): ValidationErrors | null => {
  return internationalNamePatternValidator(control, NAME_MAX_LENGTH);
};

export const strictEmailValidator = (control: AbstractControl): ValidationErrors | null => {
  const email = control.value;
  if (!new EmailValidator().isValid(email)) {
    return { email: 'INVALID_ADDRESS' };
  }

  if (containsBlockedDomains(email)) {
    return { email: 'INVALID_DOMAIN' };
  }

  return null;
};

function containsBlockedDomains(email: string): boolean {
  const emailLowerCase = email.toLowerCase();
  return BLOCKED_DOMAINS.filter((domain) => emailLowerCase.includes(domain)).length !== 0;
}
export const personValidator = (personFormGroup: AbstractControl): ValidationErrors | null => {
  const dateOfBirth = personFormGroup.get('dateOfBirth');
  const gender = personFormGroup.get('gender');
  if (!dateOfBirth || !gender) {
    return null;
  }

  const date = DateService.parseDateFromView(dateOfBirth.value);

  if (gender.value === 'PRENATAL') {
    if (!DateService.isInFuture(date)) {
      return { dateOfBirth: 'PRENATAL_TOO_EARLY' };
    }
    if (DateService.isAfterMaxPrenatalBirthdate(date)) {
      return { dateOfBirth: 'PRENATAL_TOO_LATE' };
    }
  } else if (DateService.isInFuture(date)) {
    return { dateOfBirth: 'DATE_IN_FUTURE' };
  } else if (DateService.isOver150YearsAgo(date)) {
    return { dateOfBirth: 'DATE_TOO_LONG_AGO' };
  }
  return null;
};

export const notEmptyValidator = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value || control.value.trim().length === 0) {
    return { required: true };
  }
  return null;
};

export const mobileNumberValidator = (control: AbstractControl): ValidationErrors | null => {
  const regexInternationalPrefix = /^(\+|00)(?!41)[1-9]\d+$/;
  const regexSwissMobile = /^0\d{9}$/;
  const regexSwissInternationalMobile = /^(\+|00)41\d{9}$/;
  if (!control.value) {
    return null;
  }
  const mobileNumberWithoutSpaces = (control.value as string).replace(/\s/g, '');
  const isValidMobileNumber =
    regexSwissMobile.test(mobileNumberWithoutSpaces) ||
    regexSwissInternationalMobile.test(mobileNumberWithoutSpaces) ||
    regexInternationalPrefix.test(mobileNumberWithoutSpaces);

  return isValidMobileNumber ? null : { INVALID_PHONE_NUMBER: true };
};

export const duplicatePhoneNumberValidator = (
  control: AbstractControl,
  formGroup: SubmissionDataFormGroup,
): ValidationErrors | null => {
  if (isNullOrUndefined(control.value)) {
    return null;
  }

  const mobileNumbers = formGroup.controls.persons.controls
    .map((personFormGroup) => personFormGroup.controls.phone?.value)
    .filter(isNotNullOrUndefined)
    .map((mobileNumber) => formatMobileNumberAsInternationalPrefixedByPlus(mobileNumber));

  const controlMobileNumber = formatMobileNumberAsInternationalPrefixedByPlus(control.value);

  const hasDuplicate = mobileNumbers.filter((mobileNr) => mobileNr === controlMobileNumber).length > 1;
  return hasDuplicate ? { PHONE_NUMBER_DUPLICATE: true } : null;
};

export const formatMobileNumberAsInternationalPrefixedByPlus = (mobileNumber: string): string => {
  const cleanMobileNumber = removeWhiteSpaces(mobileNumber);
  const hasInternationalPlusPrefix = cleanMobileNumber.startsWith('+');
  if (hasInternationalPlusPrefix) {
    return cleanMobileNumber;
  }
  const hasInternationalZeroPrefix = cleanMobileNumber.startsWith('00');
  if (hasInternationalZeroPrefix) {
    return cleanMobileNumber.replace(/^00/, '+');
  }

  return cleanMobileNumber.replace(/^0/, '+41');
};

const removeWhiteSpaces = (value: string): string => value.replace(/\s/g, '');

// Supports most languages and only allows for word characters. It even supports some special characters like hyphens, spaces and apostrophes
// Infos about why you find here: https://stackoverflow.com/a/45871742
const noSpecialCharsRegEx = /^[^_!¡?÷¿\/\\+=@#$%ˆ&*(){}|~<>;:[\]]+$/;

const internationalNamePatternValidator = (control: AbstractControl, maxLength: number): ValidationErrors | null => {
  if (!control.value || control.value.trim().length === 0) {
    return null;
  }
  if (control.value.trim().length < 2) {
    return { INPUT_TOO_SHORT: true };
  }
  if (control.value.trim().length > maxLength) {
    return { INPUT_TOO_LONG: true };
  }
  return noSpecialCharsRegEx.test(control.value.trim()) ? null : { SPECIAL_CHARACTERS_NOT_ALLOWED: true };
};
